import React from "react";
import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Tag,
  ArrowRight,
  MapPin,
  Phone,
  CreditCard,
  CheckCircle2,
  PlayCircle,
} from "lucide-react";
import Seo, {
  localBusinessJsonLd,
  organizationJsonLd,
  websiteJsonLd,
  faqJsonLd,
} from "@/components/Seo";
import { SectionHeading, PromoCard } from "@/components/common";
import BrandMarquee from "@/components/BrandMarquee";
import HeroCarousel from "@/components/HeroCarousel";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import {
  SITE,
  CONFIDENCE,
  BRANDS,
  SHOP_BY_BRAND_EXTRA,
  ADJUSTABLE_PROMOS,
  SIZES,
  MOBILE_EXTRA_SIZES,
  FAQS,
  HERO_IMAGE,
  PROMO_TRIO,
  MOBILE_COLLECTIONS,
  HERO_VIDEO,
} from "@/mock";

const ICONS = { Truck, ShieldCheck, Tag };

export default function Home() {
  return (
    <>
      <Seo
        title="Best Mattress Store in Fort Lauderdale, FL"
        description="MattressTown is Fort Lauderdale's premium mattress store. Shop King Koil, Beautyrest Black, Serta & Puffy with white glove delivery, a 100% comfort guarantee and a price guarantee."
        path="/"
        jsonLd={[localBusinessJsonLd(), organizationJsonLd(), websiteJsonLd(), faqJsonLd(FAQS)]}
      />

      {/* Trust icons row */}
      <section aria-label="Our promises" className="hidden border-b border-neutral-200 bg-white sm:block">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-neutral-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {CONFIDENCE.map((c) => {
            const Icon = ICONS[c.icon] || Truck;
            return (
              <div key={c.title} className="flex items-center justify-center gap-3 px-4 py-4">
                <Icon className="h-7 w-7 shrink-0 text-[#D0021B]" aria-hidden="true" />
                <span className="text-sm font-bold uppercase tracking-wide text-neutral-900">
                  {c.title}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Mobile only: reviews moved above the slider */}
      <section className="bg-neutral-100 sm:hidden">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="text-center">
            <h2 className="text-xl font-bold uppercase tracking-tight text-neutral-900">
              See What Our Customers Are Saying
            </h2>
          </div>
          <div className="mt-4">
            <ReviewsCarousel />
          </div>
        </div>
      </section>

      {/* Hero carousel - auto-rotating Fall Sale + brand banners */}
      <HeroCarousel />

      {/* Scrolling brand logo bar */}
      <BrandMarquee />

      {/* Financing banner - MattressTown card powered by Synchrony */}
      <section aria-label="Financing" className="bg-[#0f1729]">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          <div className="grid grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)] items-center gap-x-3 gap-y-6 sm:gap-x-8 sm:gap-y-10 lg:grid-cols-2 lg:gap-x-10 lg:gap-y-8">
            {/* Copy */}
            <div className="col-span-2 text-center lg:col-span-1 lg:text-left">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#ffcf3f] sm:text-sm">The MattressTown Card</p>
              <h2 className="mt-2 text-2xl font-black uppercase leading-[1.05] text-white sm:mt-3 sm:text-4xl lg:text-5xl">
                Special Financing<br className="hidden sm:block" /> Available
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-neutral-300 sm:mt-5 sm:text-base lg:mx-0 lg:text-lg">
                Bring home the sleep you deserve today and pay over time. Apply for the MattressTown card,
                powered by Synchrony, for flexible monthly payments, fast approvals and exclusive promotional offers.
              </p>
              <ul className="mx-auto mt-5 grid max-w-lg grid-cols-1 gap-2.5 text-left sm:mt-7 sm:grid-cols-2 sm:gap-3 lg:mx-0">
                {[
                  "Approvals in minutes",
                  "Flexible monthly payments",
                  "Exclusive promotional offers",
                  "No obligation to apply",
                ].map((perk) => (
                  <li key={perk} className="flex items-center gap-2.5 text-xs font-medium text-neutral-200 sm:text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#ffcf3f] sm:h-5 sm:w-5" aria-hidden="true" />
                    {perk}
                  </li>
                ))}
              </ul>
            </div>
            {/* Card - left of stacked buttons on mobile, right column on desktop */}
            <div className="col-start-1 row-start-2 flex justify-center lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:justify-end">
              <picture className="w-full sm:max-w-md lg:max-w-xl">
                <source media="(max-width: 1023px)" srcSet="/assets/mt-credit-card-tight.webp" />
                <img
                  src="/assets/mt-credit-card-navy.webp"
                  alt="MattressTown credit card powered by Synchrony"
                  loading="lazy"
                  data-testid="home-credit-card"
                  className="w-full rounded-2xl transition-transform duration-500 hover:scale-[1.03]"
                />
              </picture>
            </div>
            {/* Buttons */}
            <div className="col-start-2 row-start-2 flex flex-col gap-3 sm:gap-4 lg:col-start-1 lg:flex-row lg:flex-wrap lg:items-center lg:self-start">
              <a
                href={SITE.applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-apply-now"
                className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-[#D0021B] px-2 py-3 text-xs font-bold uppercase sm:tracking-wide text-white transition-colors hover:bg-[#a80016] sm:gap-2 sm:px-9 sm:py-4 sm:text-sm lg:w-auto"
              >
                <CreditCard className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" aria-hidden="true" /> Apply Now
              </a>
              <Link
                to="/financing"
                data-testid="financing-banner-cta"
                className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-2 border-white/40 px-2 py-2.5 text-xs font-bold uppercase sm:tracking-wide text-white transition-colors hover:border-white hover:bg-white/10 sm:gap-2 sm:px-8 sm:py-3.5 sm:text-sm lg:w-auto"
              >
                Learn More <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Fall Sale promo cards */}
      <section aria-label="Fall sale savings" className="border-b border-neutral-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-sm grid-cols-2 gap-4 sm:max-w-none sm:gap-5 lg:grid-cols-4">
            {PROMO_TRIO.map((p) => (
              <Link
                key={p.image}
                to={p.to}
                aria-label={p.alt}
                data-testid={`promo-trio-${p.image.split("/").pop().replace(".webp", "")}`}
                className="group block overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-neutral-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D0021B]"
              >
                <img
                  src={p.image}
                  alt={p.alt}
                  loading="lazy"
                  className="aspect-[848/1264] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews - continuous Google review marquee (desktop/tablet only; mobile shows it above the slider) */}
      <section className="hidden bg-neutral-100 sm:block">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold uppercase tracking-tight text-neutral-900 sm:text-3xl">
              See What Our Customers Are Saying
            </h2>
          </div>
          <div className="mt-6">
            <ReviewsCarousel />
          </div>
        </div>
      </section>

      {/* Shop by Size - bold, prominent */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          <div className="text-center">
            <p className="mb-2 text-sm font-bold uppercase tracking-widest text-[#D0021B]">Find Your Fit</p>
            <h2 className="text-3xl font-bold uppercase tracking-tight text-neutral-900 sm:text-4xl">
              Shop by Size
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-neutral-600">
              From cozy Twins to sprawling California Kings - find the perfect fit for your space.
            </p>
          </div>
          {/* Mobile: card grid (unchanged) */}
          <div className="mx-auto mt-8 grid max-w-3xl grid-cols-2 gap-5 sm:hidden">
            {[...SIZES, ...MOBILE_EXTRA_SIZES].map((s) => (
              <Link
                key={s}
                to={`/mattresses?size=${encodeURIComponent(s)}`}
                data-testid={`size-card-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex h-20 items-center justify-center rounded-2xl bg-[#D0021B] px-4 text-center text-base font-bold uppercase leading-tight tracking-wide text-white shadow-md transition-all duration-200 hover:-translate-y-1 hover:bg-[#a80016] hover:shadow-xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#D0021B]"
              >
                {s}
              </Link>
            ))}
          </div>
          {/* Desktop: compact single-line pills */}
          <div className="mt-9 hidden flex-wrap items-center justify-center gap-3 sm:flex">
            {SIZES.map((s) => (
              <Link
                key={s}
                to={`/mattresses?size=${encodeURIComponent(s)}`}
                data-testid={`size-pill-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full border border-neutral-300 bg-white px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-neutral-800 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#D0021B] hover:bg-[#D0021B] hover:text-white focus-visible:ring-2 focus-visible:ring-[#D0021B]"
              >
                {s}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured brand video (desktop) - replaces Top Brand Sales */}
      <section className="hidden bg-white sm:block">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="mb-2 text-sm font-bold uppercase tracking-widest text-[#D0021B]">Watch &amp; Learn</p>
            <h2 className="text-3xl font-bold uppercase tracking-tight text-neutral-900 sm:text-4xl">
              Experience the Brands We Carry
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-neutral-600">{HERO_VIDEO.subtitle}</p>
          </div>
          <div className="mt-10 overflow-hidden rounded-3xl bg-black shadow-2xl ring-1 ring-neutral-200">
            <video
              className="aspect-video w-full"
              controls
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={HERO_VIDEO.poster}
              data-testid="home-featured-video"
            >
              <source src={HERO_VIDEO.src} type="video/mp4" />
            </video>
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/videos"
              data-testid="see-all-videos"
              className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-9 py-4 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016]"
            >
              <PlayCircle className="h-5 w-5" aria-hidden="true" /> See All Videos
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile only: brand-grouped collections grid */}
      <section className="bg-cream sm:hidden">
        <div className="mx-auto max-w-7xl px-4 pb-6 pt-7">
          <h2 className="text-center text-2xl font-bold uppercase tracking-tight text-neutral-900">
            Shop Our Collections
          </h2>
          <div className="mt-5 space-y-6">
            {MOBILE_COLLECTIONS.map((group) => (
              <div key={group.brand}>
                <h3 className="mb-2.5 text-base font-bold uppercase tracking-tight text-[#D0021B]">
                  {group.brand}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {group.boxes.map((c) => {
                    const W = c.external ? "a" : Link;
                    const wp = c.external ? { href: c.to, target: "_blank", rel: "noopener noreferrer" } : { to: c.to };
                    return (
                    <W
                      key={c.title}
                      {...wp}
                      data-testid={`mobile-collection-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group flex flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-neutral-200 transition-all duration-200 hover:shadow-lg focus-visible:outline-none"
                    >
                      <div className="overflow-hidden">
                        <img
                          src={c.image}
                          alt={`${c.title} - ${c.cta}`}
                          loading="lazy"
                          className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                      </div>
                      <div className="flex flex-1 flex-col px-2.5 py-2">
                        <h4 className="text-sm font-bold leading-tight text-neutral-900">{c.title}</h4>
                        <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#D0021B]">
                          Shop <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                      </div>
                    </W>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Luxurious Mattress Collections - card grid (desktop/tablet) */}
      <section className="hidden bg-cream sm:block">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold uppercase tracking-tight text-neutral-900 sm:text-4xl">
            Meet Our Luxurious Mattress Collections
          </h2>
          <div className="mt-10 space-y-10">
            {MOBILE_COLLECTIONS.map((group) => (
              <div key={group.brand}>
                <h3 className="mb-5 text-center text-xl font-bold uppercase tracking-tight text-[#D0021B] sm:text-2xl">
                  {group.brand}
                </h3>
                {group.boxes.length === 1 ? (
                  (() => {
                    const c = group.boxes[0];
                    const W = c.external ? "a" : Link;
                    const wp = c.external ? { href: c.to, target: "_blank", rel: "noopener noreferrer" } : { to: c.to };
                    return (
                      <W
                        {...wp}
                        data-testid={`collection-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                        className="group grid grid-cols-5 items-stretch overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D0021B]"
                      >
                        <div className="col-span-2 overflow-hidden">
                          <img
                            src={c.image}
                            alt={`${c.title} - ${c.cta}`}
                            loading="lazy"
                            className="h-full min-h-[13rem] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                          />
                        </div>
                        <div className="col-span-3 flex flex-col justify-center px-8 py-7">
                          <h4 className="text-2xl font-bold text-neutral-900">{c.title}</h4>
                          <p className="mt-2 text-neutral-600">{c.subtitle}</p>
                          <span className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-neutral-900 px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-colors group-hover:bg-[#D0021B]">
                            {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                          </span>
                        </div>
                      </W>
                    );
                  })()
                ) : (
                  <div className="grid grid-cols-2 gap-5 lg:grid-cols-3">
                    {group.boxes.map((c) => {
                      const W = c.external ? "a" : Link;
                      const wp = c.external ? { href: c.to, target: "_blank", rel: "noopener noreferrer" } : { to: c.to };
                      return (
                        <W
                          key={c.title}
                          {...wp}
                          data-testid={`collection-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                          className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D0021B]"
                        >
                          <div className="overflow-hidden">
                            <img
                              src={c.image}
                              alt={`${c.title} - ${c.cta}`}
                              loading="lazy"
                              className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                            />
                          </div>
                          <div className="flex flex-1 flex-col px-5 py-4">
                            <h4 className="text-lg font-bold text-neutral-900">{c.title}</h4>
                            <p className="mt-1 text-sm text-neutral-600">{c.subtitle}</p>
                            <span className="mt-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-neutral-900 transition-colors group-hover:text-[#D0021B]">
                              {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                            </span>
                          </div>
                        </W>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Support - adjustable set promo cards (desktop/tablet; mobile uses the Adjustable Sets collection box) */}
      <section className="hidden bg-cream sm:block">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          <div className="text-center">
            <p className="mb-2 text-sm font-bold uppercase tracking-widest text-[#D0021B]">
              Adjustable Bases &amp; Mattress Sets
            </p>
            <h2 className="text-3xl font-bold uppercase tracking-tight text-neutral-900 sm:text-4xl">
              Advanced Support with <span className="text-[#D0021B]">Adjustable Sets</span>
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-neutral-600">
              Elevate your comfort with premium adjustable mattress sets - zero-gravity positioning,
              massage and app control from the brands you trust.
            </p>
          </div>
          <div className="mx-auto mt-10 max-w-4xl">
            {ADJUSTABLE_PROMOS.map((item) => (
              <PromoCard key={item.brand} item={item} />
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/adjustable-bases"
              className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016]"
            >
              Shop Adjustable Sets <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Shop by Brand (hidden on mobile per client) */}
      <section className="hidden bg-white sm:block">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          <SectionHeading eyebrow="Premium Name Brands" title="Shop by Brand" center />
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="shop-by-brand-grid">
            {[...BRANDS.map((b) => ({ ...b, to: `/brands/${b.slug}` })), ...SHOP_BY_BRAND_EXTRA].map((b) => (
              <Link
                key={b.slug}
                to={b.to}
                data-testid={`shop-brand-${b.slug}`}
                className="group relative flex items-center justify-center rounded-2xl border border-neutral-200 bg-white p-1.5 text-center hover-lift focus-visible:ring-2 focus-visible:ring-[#D0021B]"
              >
                <img
                  src={`/brands/mono/${b.slug}.png`}
                  alt={`${b.name} logo`}
                  loading="lazy"
                  width="600"
                  height="300"
                  className="aspect-[2/1] h-auto w-full object-contain"
                />
                <span className="pointer-events-none absolute bottom-1 left-0 right-0 text-[10px] font-bold uppercase tracking-wide text-[#D0021B] opacity-0 transition-opacity group-hover:opacity-100">
                  Shop →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Location band */}
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          <div className="grid items-stretch gap-6 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm sm:gap-0 lg:grid-cols-5">
            <img
              src={SITE.storePhotos[1]}
              alt="MattressTown storefront in Fort Lauderdale, Florida"
              loading="lazy"
              className="h-64 w-full object-cover sm:h-80 lg:col-span-3 lg:h-full lg:min-h-[540px]"
            />
            <div className="p-8 lg:col-span-2 lg:p-12">
              <p className="mb-2 text-sm font-bold uppercase tracking-widest text-[#D0021B]">Visit Us</p>
              <h2 className="text-3xl font-bold uppercase text-neutral-900">Your Local Fort Lauderdale Showroom</h2>
              <p className="mt-4 text-neutral-600">{SITE.address.full}</p>
              <ul className="mt-4 space-y-1 text-sm text-neutral-600">
                {SITE.hours.map((h) => (
                  <li key={h.day}><span className="font-semibold">{h.day}:</span> {h.time}</li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-4">
                <a
                  href={SITE.address.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-7 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"
                >
                  <MapPin className="h-4 w-4" aria-hidden="true" /> Get Directions
                </a>
                <a
                  href={SITE.phoneHref}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-neutral-900 px-7 py-3 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-900 hover:text-white"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          <SectionHeading eyebrow="Good to Know" title="Frequently Asked Questions" center />
          {/* Mobile: collapsed accordion (question only until tapped) */}
          <div className="mt-10 divide-y divide-neutral-200 border-y border-neutral-200 sm:hidden">
            {FAQS.map((f) => (
              <details key={f.q} className="group py-4" data-testid={`faq-mobile-${f.q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-base font-bold text-neutral-900 [&::-webkit-details-marker]:hidden">
                  <span>{f.q}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 rotate-90 text-[#D0021B] transition-transform group-open:-rotate-90" aria-hidden="true" />
                </summary>
                <p className="mt-2 leading-relaxed text-neutral-600">{f.a}</p>
              </details>
            ))}
          </div>
          {/* Desktop/tablet: collapsible accordion */}
          <div className="mt-10 hidden divide-y divide-neutral-200 border-y border-neutral-200 sm:block">
            {FAQS.map((f) => (
              <details key={f.q} className="group py-5" data-testid={`faq-${f.q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-lg font-bold text-neutral-900 [&::-webkit-details-marker]:hidden">
                  <span>{f.q}</span>
                  <ArrowRight className="h-5 w-5 shrink-0 rotate-90 text-[#D0021B] transition-transform group-open:-rotate-90" aria-hidden="true" />
                </summary>
                <p className="mt-3 leading-relaxed text-neutral-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
